import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Container, Typography, Card, CardContent, Button, Box, AppBar, Toolbar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, Alert, Grid, CircularProgress, Select, MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { LanguageContext } from '../App';
import { LANGUAGES, getText } from '../utils/translations';
import logo from '../assets/logo.png';

function RecommendationView() {
    const navigate = useNavigate();
    const { farmerId, soilDataId } = useParams();
    const [farmer, setFarmer] = useState(null);
    const [soilData, setSoilData] = useState(null);
    const [recommendation, setRecommendation] = useState(null);
    const [govtDosage, setGovtDosage] = useState(null);
    const [additionalData, setAdditionalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { language, setLanguage } = useContext(LanguageContext);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [farmerId, soilDataId]);

    const fetchData = async () => {
        try {
            const farmerRes = await axios.get(`/api/farmers/${farmerId}`);
            const soilRes = await axios.get(`/api/soil-data/${soilDataId}`);
            setFarmer(farmerRes.data);
            setSoilData(soilRes.data);

            const recRes = await axios.post('/api/recommendations/generate', {
                farmer_id: parseInt(farmerId),
                soil_data_id: parseInt(soilDataId),
            });
            setRecommendation(recRes.data);



            // Fetch Government-Verified Dosage - ENSURE THIS ALWAYS WORKS
            try {
                // Validate and prepare all required fields with defaults
                const fertRequest = {
                    state: farmerRes.data.state || 'Uttar Pradesh',
                    district: farmerRes.data.district || 'Lucknow',
                    crop: farmerRes.data.crop_name || 'Wheat',
                    season: farmerRes.data.crop_season || 'Rabi',
                    field_area_hectares: parseFloat(farmerRes.data.field_area) || 1.0,
                    soil_nitrogen: parseFloat(soilRes.data.nitrogen) || 0,
                    soil_phosphorus: parseFloat(soilRes.data.phosphorus) || 0,
                    soil_potassium: parseFloat(soilRes.data.potassium) || 0,
                    organic_carbon: parseFloat(soilRes.data.organic_carbon) || 0.5
                };

                console.log('✅ Fetching government fertilizer for:', fertRequest);
                const govtRes = await axios.post('/api/government/calculate-fertilizer', fertRequest);
                console.log('✅ Fertilizer plan received successfully');

                // Check if season fallback was used and log it
                if (govtRes.data.data.season_fallback_used) {
                    console.warn('⚠️ Season Fallback:', govtRes.data.data.fallback_notice);
                }

                setGovtDosage(govtRes.data.data);
            } catch (govErr) {
                console.error('❌ Fertilizer API failed:', govErr);
                console.error('Error details:', govErr.response?.data);
                // Don't block the UI - just log the error
            }

            // Parse additional data from reasoning JSON
            try {
                const jsonMatch = recRes.data.reasoning.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    setAdditionalData(parsed);
                }
            } catch (e) {
                console.error('Failed to parse additional data:', e);
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string'
                ? detail
                : (typeof detail === 'object' ? JSON.stringify(detail) : 'Failed to load data');
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Advanced Health Score Logic (Distance from Optimal)
    // Unified Status mapping to backend labels
    const getStatus = (value, param, breakdown = null) => {
        // If we have backend scores_breakdown, use it for consistency
        if (breakdown && breakdown[param]) {
            const data = breakdown[param];
            const score = data.score;
            let color = 'success';

            // Score-based coloring (Primary mapping)
            if (score < 45) color = 'error';       // Red: Critical/Very Low (<45%)
            else if (score < 80) color = 'warning'; // Orange: Marginal/Sub-optimal (45-79%)
            else color = 'success';                // Green: Optimal/Good (>=80%)

            // Status-based Overrides
            const status = data.status || '';
            if (status.includes('Critical') || status.includes('Excessive') || status.includes('Strongly')) {
                color = 'error';
            } else if (status.includes('High') && param !== 'organic_carbon') {
                color = score >= 85 ? 'success' : 'warning';
            }

            return { label: status, color, percent: score };
        }

        // Fallback: calculate from raw values if breakdown not available (Simplified ICAR-like logic)
        if (param === 'nitrogen' || param === 'phosphorus' || param === 'potassium') {
            if (value < 140) return { label: 'Low', color: 'error', percent: Math.min(40, (value / 140) * 40) };
            if (value < 280) return { label: 'Sub-Optimal', color: 'warning', percent: 65 };
            return { label: 'Optimal', color: 'success', percent: 95 };
        }

        if (param === 'ph') {
            if (value < 5.5 || value > 8.5) return { label: 'Critical', color: 'error', percent: 30 };
            if (value < 6.5 || value > 7.5) return { label: 'Marginal', color: 'warning', percent: 65 };
            return { label: 'Optimal', color: 'success', percent: 100 };
        }

        if (param === 'ec') {
            if (value > 2.0) return { label: 'Saline', color: 'error', percent: 40 };
            if (value > 0.8) return { label: 'Slightly Saline', color: 'warning', percent: 70 };
            return { label: 'Normal', color: 'success', percent: 100 };
        }

        if (param === 'organic_carbon') {
            if (value < 0.5) return { label: 'Low', color: 'error', percent: (value / 0.5) * 40 };
            if (value < 0.75) return { label: 'Medium', color: 'warning', percent: 70 };
            return { label: 'High (Optimal)', color: 'success', percent: 100 };
        }

        return { label: 'Optimal', color: 'success', percent: 100 };
    };

    // Helper for Circular Gauge
    const CircularGauge = ({ value, color, label }) => {
        // Map color string to hex
        const colorMap = {
            success: '#4CAF50',
            warning: '#FF9800',
            error: '#F44336',
            info: '#2196F3'
        };
        const hexColor = colorMap[color] || '#4CAF50';

        return (
            <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                        variant="determinate"
                        value={100}
                        size={50}
                        sx={{ color: 'rgba(255, 255, 255, 0.1)' }}
                    />
                    <CircularProgress
                        variant="determinate"
                        value={value}
                        size={50}
                        sx={{
                            color: hexColor,
                            position: 'absolute',
                            left: 0,
                            '& .MuiCircularProgress-circle': { strokeLinecap: 'round' }
                        }}
                    />
                    <Box
                        sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant="caption" component="div" sx={{ color: hexColor, fontWeight: 'bold' }}>
                            {Math.round(value || 0)}%
                        </Typography>
                    </Box>
                </Box>
                <Typography variant="caption" sx={{ mt: 0.5, color: hexColor, fontWeight: 600 }}>
                    {label}
                </Typography>
            </Box>
        );
    };

    const handleDownloadCSV = () => {
        if (!recommendation) return;
        window.open(`http://localhost:8080/api/soil-health-card/${recommendation.id}/csv`, '_blank');
    };

    const handleDownloadPDF = () => {
        if (!recommendation) return;
        window.open(`http://localhost:8080/api/soil-health-card/${recommendation.id}/pdf`, '_blank');
    };

    const handleDownloadExcel = () => {
        if (!recommendation) return;
        window.open(`http://localhost:8080/api/soil-health-card/${recommendation.id}/excel`, '_blank');
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={60} sx={{ color: '#4CAF50' }} />
                <Typography className="text-shimmer" sx={{ ml: 2 }}>Loading Analysis...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', pb: 8 }}>
            <AppBar position="sticky" elevation={0} sx={{ top: 0, zIndex: 1100, backgroundColor: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(20px)', borderBottom: 'none' }}>
                <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
                    <img src={logo} alt="SoilSense Logo"
                        style={{ height: '40px', marginRight: '8px' }} />
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
                        <span className="text-shimmer">{getText('soil_health_card', language)}</span>
                    </Typography>

                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                        <Select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            size="small"
                            sx={{
                                color: '#4CAF50',
                                mr: 2,
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 175, 80, 0.5)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50' }
                            }}
                        >
                            {LANGUAGES.map((lang) => (
                                <MenuItem key={lang.code} value={lang.code}>{lang.nativeName}</MenuItem>
                            ))}
                        </Select>
                        <Button color="inherit" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadCSV} sx={{ mr: 1, borderRadius: '20px' }}>CSV</Button>
                        <Button color="inherit" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadPDF} sx={{ mr: 1, borderRadius: '20px' }}>PDF</Button>
                        <Button color="inherit" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadExcel} sx={{ mr: 2, borderRadius: '20px' }}>Excel</Button>
                    </Box>

                    {/* Mobile Menu Icon or simplified buttons can go here if needed, but for now just back button */}
                    <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ borderRadius: '20px', fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                        {getText('back_dashboard', language)}
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ mt: 4 }} component={motion.div} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                {error && <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.1)', backdropFilter: 'blur(10px)' }}>{typeof error === 'string' ? error : 'Failed to load data.'}</Alert>}

                {/* Farmer Info */}
                {farmer && (
                    <Card className="premium-card" sx={{ mb: 4 }}>
                        <CardContent>
                            <Typography variant="h4" className="text-shimmer" sx={{ mb: 1 }}>{farmer.name}</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body1" sx={{ color: '#e0e0e0', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                                        📍 {farmer.village}, {farmer.district}, {farmer.state}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body1" sx={{ color: '#e0e0e0', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                                        🌾 <strong>{farmer.field_area} ha</strong> | {farmer.crop_name} ({farmer.crop_season})
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Sugarcane Specialized Dashboard Link */}
                            {farmer.crop_name.toLowerCase() === 'sugarcane' && (
                                <Box sx={{ mt: 3, p: 2, borderRadius: 2, background: 'linear-gradient(90deg, rgba(0, 188, 212, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%)', border: '1px solid rgba(0, 188, 212, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ color: '#00BCD4', fontWeight: 'bold' }}>🎋 Specialized Sugarcane Management Available</Typography>
                                        <Typography variant="body2" sx={{ color: '#b0bec5' }}>Monitor real-time moisture, water stress, and split-dose fertilizer schedules.</Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => navigate(`/sugarcane-dashboard/${farmerId}`)}
                                        sx={{
                                            background: 'linear-gradient(45deg, #00BCD4 30%, #4CAF50 90%)',
                                            color: 'white',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Open Management Dashboard
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* TABLE 1: Soil Health Summary with GAUGES */}
                {soilData && (
                    <Card className="premium-card" sx={{ mb: 4 }}>
                        <CardContent>
                            <Typography variant="h5" sx={{ mb: 3, color: '#4CAF50', fontWeight: 'bold' }}>
                                🔬 {getText('soil_health_analysis', language)}
                            </Typography>
                            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', boxShadow: 'none', overflowX: 'auto' }}>
                                <Table sx={{ minWidth: 600 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: '#81C784', fontWeight: 'bold', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>{getText('parameter', language)}</TableCell>
                                            <TableCell align="right" sx={{ color: '#81C784', fontWeight: 'bold', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>{getText('value', language)}</TableCell>
                                            <TableCell align="center" sx={{ color: '#81C784', fontWeight: 'bold', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>{getText('health_score', language)}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(() => {
                                            const breakdown = additionalData?.scores_breakdown || {};

                                            return [
                                                { label: 'Nitrogen (N)', value: soilData.nitrogen, unit: 'mg/kg', param: 'nitrogen' },
                                                { label: 'Phosphorus (P)', value: soilData.phosphorus, unit: 'mg/kg', param: 'phosphorus' },
                                                { label: 'Potassium (K)', value: soilData.potassium, unit: 'mg/kg', param: 'potassium' },
                                                { label: 'pH Level', value: soilData.ph, unit: '', param: 'ph' },
                                                { label: 'EC (Salinity)', value: soilData.ec, unit: 'dS/m', param: 'ec' },
                                                { label: 'Organic Carbon', value: soilData.organic_carbon, unit: '%', param: 'organic_carbon' },
                                                { label: 'Soil Moisture', value: soilData.moisture, unit: '%', param: 'moisture' },
                                            ].map((row, idx) => {
                                                if (row.value === null || row.value === undefined) return null;
                                                const status = getStatus(row.value, row.param, breakdown);
                                                return (
                                                    <TableRow key={idx} className="border-glow" sx={{ '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' } }}>
                                                        <TableCell sx={{ color: '#fff', fontSize: { xs: '0.9rem', sm: '1.1rem' }, py: 2 }}>{row.label}</TableCell>
                                                        <TableCell align="right" sx={{ color: '#fff', fontSize: { xs: '1rem', sm: '1.2rem' }, fontWeight: 600 }}>
                                                            {row.value} {row.unit && <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{row.unit}</span>}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <CircularGauge value={status.percent} color={status.color} label={status.label} />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            });
                                        })()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}



                {/* UNIFIED GOVERNMENT-VERIFIED FERTILIZER RECOMMENDATION */}
                {govtDosage && farmer && (
                    <Card className="premium-card" sx={{ mb: 4, border: '1px solid rgba(0, 188, 212, 0.3)' }} component={motion.div} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <CardContent>
                            {/* Land Area & Crop Info Box */}
                            <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(0, 188, 212, 0.05)', border: '1px solid rgba(0, 188, 212, 0.2)' }}>
                                <Typography variant="body2" sx={{ color: '#e0f7fa' }}>
                                    📍 <strong>Land:</strong> {farmer.field_area} ha |
                                    🌾 <strong>Crop:</strong> {farmer.crop_name} ({farmer.crop_season}) |
                                    📍 <strong>State:</strong> {farmer.state}
                                </Typography>
                            </Alert>

                            {/* Title & Badge */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                                <Typography variant="h5" sx={{ color: '#00BCD4', fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                                    💊 Fertilizer Plan for {farmer.crop_name}
                                </Typography>
                                <Chip
                                    label="Govt. Reference Based"
                                    size="small"
                                    sx={{ backgroundColor: 'rgba(0, 188, 212, 0.2)', color: '#00BCD4', fontWeight: 'bold', ml: { sm: 'auto' } }}
                                />
                            </Box>

                            {/* Season Fallback Notice - Non-intrusive inline warning */}
                            {govtDosage.season_fallback_used && govtDosage.fallback_notice && (
                                <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(255, 193, 7, 0.1)', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                                    <Typography variant="caption" sx={{ color: '#fff' }}>
                                        ⚠️ {govtDosage.fallback_notice}
                                    </Typography>
                                </Alert>
                            )}

                            {/* Main Fertilizer Table with Government Price Column */}
                            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: '#00BCD4', fontWeight: 'bold' }}>Fertilizer</TableCell>
                                            <TableCell align="right" sx={{ color: '#00BCD4', fontWeight: 'bold' }}>Recommended Quantity (kg)</TableCell>
                                            <TableCell align="right" sx={{ color: '#00BCD4', fontWeight: 'bold' }}>Govt. Price/50kg</TableCell>
                                            <TableCell align="right" sx={{ color: '#00BCD4', fontWeight: 'bold' }}>Estimated Cost</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {['Urea', 'DAP', 'SSP', 'MOP'].map((fert) => {
                                            const total = govtDosage.total_field[fert];
                                            const cost = govtDosage.total_field_costs[fert];
                                            const govtPrice = govtDosage.govt_prices?.[fert];

                                            if (total <= 0) return null;

                                            return (
                                                <TableRow key={fert} sx={{ '&:hover': { backgroundColor: 'rgba(0, 188, 212, 0.1)' } }}>
                                                    <TableCell sx={{ color: '#fff', fontWeight: 500, fontSize: '1rem' }}>
                                                        {fert}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: '#00BCD4', fontWeight: 'bold' }}>
                                                        {total.toFixed(2)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: '#FFB74D', fontWeight: 'bold' }}>
                                                        ₹{govtPrice || 'N/A'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: '#81C784', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                        ₹{cost.toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        <TableRow sx={{ backgroundColor: 'rgba(0, 188, 212, 0.2)' }}>
                                            <TableCell colSpan={3} sx={{ color: '#00BCD4', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                                TOTAL ESTIMATED COST
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#00BCD4', fontWeight: 'bold', fontSize: '1.5rem', textShadow: '0 0 10px rgba(0, 188, 212, 0.5)' }}>
                                                ₹{govtDosage.total_estimated_cost?.toLocaleString()}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Biofertilizer Footnote */}
                            {govtDosage.biofertilizer?.recommended && (
                                <Typography variant="caption" sx={{ mt: 2, display: 'block', color: '#888' }}>
                                    * Biofertilizer: {govtDosage.biofertilizer.total_field_packets} packets
                                    (standard packet size: 200g). May be available through government programs.
                                </Typography>
                            )}


                            {/* Department of Fertilizers Disclaimer with State */}
                            <Alert severity="warning" sx={{ mt: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', '& .MuiAlert-icon': { color: '#FFB74D' } }}>
                                <Typography variant="caption" sx={{ color: '#e0e0e0' }}>
                                    <strong>Price Source:</strong> Department of Fertilizers, GoI (NBS Scheme) •
                                    <strong> State:</strong> {farmer.state} •
                                    Prices may vary by district and dealer
                                    {govtDosage.price_last_updated && (
                                        <span> • Updated: {govtDosage.price_last_updated}</span>
                                    )}
                                </Typography>
                            </Alert>

                            {/* Application Schedule */}
                            {govtDosage.application_schedule && (
                                <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(0, 0, 0, 0.3)', borderRadius: 2 }}>
                                    <Typography variant="subtitle1" sx={{ color: '#00BCD4', fontWeight: 'bold', mb: 1 }}>
                                        📅 Application Schedule
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {govtDosage.application_schedule.map((slot, i) => (
                                            <Grid item xs={12} sm={6} key={i}>
                                                <Box sx={{ p: 1, borderLeft: '3px solid #00BCD4', pl: 2 }}>
                                                    <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>{slot.stage} ({slot.percentage}%)</Typography>
                                                    <Typography variant="body2" sx={{ color: '#aaa' }}>{slot.fertilizers}</Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}


                {/* BIOFERTILIZER RECOMMENDATION BANNER */}
                {additionalData?.biofertilizer_recommended && (
                    <Card className="premium-card" sx={{ mb: 4, backgroundColor: 'rgba(139, 69, 19, 0.15)', borderColor: 'rgba(139, 69, 19, 0.5)' }} component={motion.div} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.25 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{
                                    backgroundColor: 'rgba(139, 69, 19, 0.3)',
                                    borderRadius: '50%',
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Typography sx={{ fontSize: '2.5rem' }}>🦠</Typography>
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h5" sx={{ color: '#D2691E', fontWeight: 'bold', mb: 1 }}>
                                        Biofertilizer Recommended
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#e0e0e0', mb: 1 }}>
                                        <strong>Organic carbon levels are low.</strong> Use biofertilizers to improve soil health naturally!
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#aaa' }}>
                                        ✓ Recommended: Rhizobium, Azotobacter, or Azospirillum
                                        • Apply 15-20 days before sowing
                                        • Enhances nitrogen fixation & organic matter
                                    </Typography>
                                </Box>
                                <Chip
                                    label="ECO-FRIENDLY"
                                    sx={{
                                        backgroundColor: 'rgba(76, 175, 80, 0.3)',
                                        color: '#4CAF50',
                                        fontWeight: 'bold',
                                        fontSize: '0.9rem',
                                        px: 2
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* TABLE 3: Application Schedule */}
                {additionalData?.application_schedule && (
                    <Card className="premium-card" sx={{ mb: 4 }} component={motion.div} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                        <CardContent>
                            <Typography variant="h5" sx={{ mb: 3, color: '#4CAF50', fontWeight: 'bold' }}>
                                📅 {getText('app_schedule', language)}
                            </Typography>
                            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: '#81C784', fontWeight: 'bold' }}>{getText('stage', language)}</TableCell>
                                            <TableCell sx={{ color: '#81C784', fontWeight: 'bold' }}>{getText('time', language)}</TableCell>
                                            <TableCell sx={{ color: '#81C784', fontWeight: 'bold' }}>{getText('fertilizer', language)}</TableCell>
                                            <TableCell align="right" sx={{ color: '#81C784', fontWeight: 'bold' }}>Quantity (%)</TableCell>
                                            <TableCell sx={{ color: '#81C784', fontWeight: 'bold' }}>{getText('note', language)}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {additionalData.application_schedule.map((stage, index) => (
                                            <TableRow key={index} className="border-glow" sx={{ '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' } }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 500 }}>{stage.stage}</TableCell>
                                                <TableCell sx={{ color: '#e0e0e0' }}>{stage.time}</TableCell>
                                                <TableCell sx={{ color: '#81C784' }}>{stage.fertilizer}</TableCell>
                                                <TableCell align="right" sx={{ color: '#e0e0e0' }}>{stage.quantity_percent}%</TableCell>
                                                <TableCell sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>{stage.note}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}

                {/* TABLE 4: Soil Improvement Advisory */}
                {additionalData?.soil_improvements && (
                    <Card className="premium-card" sx={{ mb: 4 }} component={motion.div} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
                        <CardContent>
                            <Typography variant="h5" sx={{ mb: 3, color: '#4CAF50', fontWeight: 'bold' }}>
                                🌱 {getText('improvement_advisory', language)}
                            </Typography>
                            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: '#81C784', fontWeight: 'bold' }}>{getText('issue', language)}</TableCell>
                                            <TableCell sx={{ color: '#81C784', fontWeight: 'bold' }}>{getText('recommendation', language)}</TableCell>
                                            <TableCell align="right" sx={{ color: '#81C784', fontWeight: 'bold' }}>{getText('quantity', language)}</TableCell>
                                            <TableCell sx={{ color: '#81C784', fontWeight: 'bold' }}>{getText('purpose', language)}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {additionalData.soil_improvements.map((imp, index) => (
                                            <TableRow key={index} className="border-glow" sx={{ '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' } }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 500 }}>{imp.issue}</TableCell>
                                                <TableCell sx={{ color: '#81C784' }}>{imp.recommendation}</TableCell>
                                                <TableCell align="right" sx={{ color: '#e0e0e0' }}>{imp.quantity}</TableCell>
                                                <TableCell sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>{imp.purpose}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}

            </Container>

            {/* Footer */}
            <Box
                sx={{
                    textAlign: 'center',
                    py: 3,
                    borderTop: '1px solid rgba(76, 175, 80, 0.2)',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: '#81C784',
                        fontWeight: 500,
                        letterSpacing: '0.5px'
                    }}
                >
                    <Typography
                        variant="body2"
                        component="a"
                        href="https://github.com/Paldeepak079/SoilSense---Smart-Fertilizer-Recommendation-System"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: '#81C784',
                            fontWeight: 500,
                            letterSpacing: '0.5px',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#A5D6A7',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        Crafted by Team_MADTech
                    </Typography>
                </Typography>
            </Box>
        </Box>
    );
}

export default RecommendationView;
